import { defineStore } from "pinia";
import _ from "lodash";
import * as _debug from "debug";
import ApiClient from "@/lib/ApiClient";
import { sureClone } from "@/lib/DataUtils";
import { createStore } from "./storeHelper";
import { findFirst } from "obj-traverse/lib/obj-traverse";

const debug = _debug(`${process.env.VUE_APP_PREFIX}:CatalogStore`);

const DEFAULT_EDITOR = {
  id: null,
  parentId: null,
  templateItemId: null,
  checklistRowsToImportText: "",
};

const DEFAULT_COLLECTION_EDITOR = {
  name: null,
  description: null,
  organisationId: null,
};

const DEFAULT_CATEGORY_EDITOR = {
  name: null,
  information: null,
  printToPdfDefault: true,
};

const DEFAULT_CHECKLIST_EDITOR = {
  name: null,
  isCrossCheckList: false,
  isInformationOnly: false,
  printToPdfDefault: true,
};

const DEFAULT_CHECKLISTROW_EDITOR = {
  text: null,
  information: null,
  imageFileName: null,
};

export const useCatalogStore = defineStore(
  "catalog",
  createStore({
    state: {
      catalog: {},
      pdfDownloadUrl: null,
      importWithParentId: null,
      editor: sureClone(DEFAULT_EDITOR),
    },

    getters: {
      catalogList: (state) => _.values(state.catalog),
      catalogItemById: (state) => (catalogItemId) => {
        const values = _.values(state.catalog);
        let first;
        for (const v of values) {
          first = findFirst(v, "children", { id: catalogItemId });
          if (first) {
            break;
          }
        }
        return first;
      },
      catalogItemByTargetId: (state) => (targetId) => {
        const values = _.values(state.catalog);
        let first;
        for (const v of values) {
          if (v.targetId === targetId) {
            first = v;
            break;
          }
          first = findFirst(v, "children", { targetId: targetId });
          if (first) {
            break;
          }
        }
        return first;
      },
    },

    actions: {
      addCatalogItem(catalogItem) {
        this.catalog[catalogItem.id] = catalogItem;
      },

      clearCatalog() {
        this.catalog = {};
      },

      deleteCatalogItemFromState(catalogItemId) {
        delete this.catalog[catalogItemId];
      },

      newCatalogItemToEditor() {
        this.set("editor", sureClone(DEFAULT_EDITOR));
      },

      newCollectionToCatalogItemEditor() {
        this.set("editor.collection", sureClone(DEFAULT_COLLECTION_EDITOR));
      },

      newCategoryToCatalogItemEditor() {
        this.set("editor.category", sureClone(DEFAULT_CATEGORY_EDITOR));
      },

      newChecklistRowToCatalogItemEditor() {
        this.set("editor.checklistRow", sureClone(DEFAULT_CHECKLISTROW_EDITOR));
      },

      newChecklistToCatalogItemEditor() {
        this.set("editor.checklist", sureClone(DEFAULT_CHECKLIST_EDITOR));
      },

      async load() {
        debug("load");
        const catalog = await ApiClient.get(`/catalog/v2`);
        debug("load", catalog);
        this.set("catalog", _.keyBy(catalog, "targetId"));
      },

      async getPdf(pdfParameters) {
        debug(
          "getPdf for collection: " +
            pdfParameters.collection +
            ", parameters: " +
            pdfParameters.parameters,
        );
        const pdfUrl = await ApiClient.getFileByPostMethod(
          `/pdf?collectionId=${pdfParameters.collection}`,
          pdfParameters.parameters,
        );
        debug("getPdf", pdfUrl);
        this.set("pdfDownloadUrl", pdfUrl);
      },

      async moveUpCatalogItem(catalogItemId) {
        debug("moveUpCatalogItem", catalogItemId);
        const result = await ApiClient.post(
          `/catalogItems/${catalogItemId}/MoveUp`,
          catalogItemId,
        );
        debug("moveUpCatalogItem, result", result);
      },

      async moveDownCatalogItem(catalogItemId) {
        debug("moveDownCatalogItem", catalogItemId);
        const result = await ApiClient.post(
          `/catalogItems/${catalogItemId}/MoveDown`,
          catalogItemId,
        );
        debug("moveDownCatalogItem, result", result);
      },

      async createCatalogItem() {
        const catalogItem = sureClone(this.editor);
        _.unset(catalogItem, "id");
        debug("createCatalogItem", catalogItem);
        const response = await ApiClient.post("/catalogItems", catalogItem);
        debug("createCatalogItem - response", response);

        const parentItem = this.catalogItemById(response.parentId);

        // Handle category creation
        if (response.category != null) {
          const { useCategoriesStore } = await import("./categories");
          const categoriesStore = useCategoriesStore();
          categoriesStore.addCategory(response.category);

          const newItemCategory = {
            id: response.id,
            information: response.category.information ?? null,
            isFirstInSequence: false,
            isLastInSequence: true,
            name: response.category.name,
            parentId: response.parentId,
            restrictionMode: "Uncontrolled",
            sequenceNumber: response.sequenceNumber,
            targetId: response.category.id,
            type: "CATEGORY",
            children: [],
          };
          debug("new category tree item to be added is ", newItemCategory);
          parentItem.children.push(newItemCategory);
        }

        // Handle checklist creation
        if (response.checklist != null) {
          const { useChecklistsStore } = await import("./checklists");
          const checklistsStore = useChecklistsStore();
          checklistsStore.addChecklist(response.checklist);

          const newItemChecklist = {
            id: response.id,
            information: response.checklist.information ?? null,
            isFirstInSequence: false,
            isLastInSequence: true,
            name: response.checklist.name,
            parentId: response.parentId,
            restrictionMode: "Uncontrolled",
            sequenceNumber: response.sequenceNumber,
            targetId: response.checklist.id,
            type: "CHECKLIST",
            children: [],
          };
          debug("new checklist tree item to be added is ", newItemChecklist);
          parentItem.children.push(newItemChecklist);
        }

        // Handle checklist row creation
        if (response.checklistRow != null) {
          const { useChecklistRowsStore } = await import("./checklistRows");
          const checklistRowsStore = useChecklistRowsStore();
          checklistRowsStore.addChecklistRow(response.checklistRow);

          const newItemChecklistRow = {
            id: response.id,
            information: response.checklistRow.information ?? null,
            isFirstInSequence: false,
            isLastInSequence: true,
            name: response.checklistRow.name,
            parentId: response.parentId,
            restrictionMode: "Uncontrolled",
            sequenceNumber: response.sequenceNumber,
            targetId: response.checklistRow.id,
            type: "CHECKLISTROW",
            children: [],
          };
          debug(
            "new checklistrow tree item to be added is ",
            newItemChecklistRow,
          );
          parentItem.children.push(newItemChecklistRow);
        }

        const previousItem = parentItem.children?.find(
          (x) => x.sequenceNumber == response.sequenceNumber - 1,
        );
        if (previousItem != null) {
          previousItem.isLastInSequence = false;
        }

        return response;
      },

      async loadCatalogItemToEditor(catalogItemId) {
        debug("loadCatalogItemToEditor", catalogItemId);
        const catalogItem = this.catalogItemById(catalogItemId);
        debug("catalogItem", catalogItem);
        if (!catalogItem) {
          throw new Error("Catalog item not found");
        }

        this.set("editor", sureClone(catalogItem));

        if (catalogItem.type === "CATEGORY") {
          const { useCategoriesStore } = await import("./categories");
          const categoriesStore = useCategoriesStore();
          const category = categoriesStore.categoryById(catalogItem.targetId);
          this.set("editor.category", sureClone(category));
        }

        if (catalogItem.type === "CHECKLISTROW") {
          const { useChecklistRowsStore } = await import("./checklistRows");
          const checklistRowsStore = useChecklistRowsStore();
          const checklistRow = checklistRowsStore.checklistRowById(
            catalogItem.targetId,
          );
          this.set("editor.checklistRow", sureClone(checklistRow));
        }
      },

      async copyCatalogItem({ catalogItemId, targetCatalogItemId }) {
        debug("copyCatalogItem", catalogItemId);
        const response = await ApiClient.post(
          `/catalogItems/${catalogItemId}/clone?TargetId=${targetCatalogItemId}`,
        );
        debug("copyCatalogItem - response", response);
        this.addCatalogItem(response);
      },

      async syncChangesInTarget(data) {
        const catalogItem = this.catalogItemByTargetId(data.id);
        if (catalogItem) {
          catalogItem.name = data.name;
        }
      },

      async removeTemplateLinkingFromCatalogItem(catalogItemId) {
        debug("removeTemplateLinkingFromCatalogItem", catalogItemId);
        const result = await ApiClient.post(
          `/catalogItems/${catalogItemId}/remove-template-linking`,
        );
        debug("removeTemplateLinkingFromCatalogItem, result", result);
        if (result) {
          result.forEach((element) => {
            const catalogItem = this.catalogItemById(element.id);
            if (catalogItem) {
              catalogItem.isLinkedToTemplate = false;
            }
          });
        }
      },

      async deleteCatalogItem(catalogItemId) {
        debug("deleteCatalogItem", catalogItemId);
        const result = await ApiClient.delete(`/catalogItems/${catalogItemId}`);
        debug("deleteCatalogItem - result", result);
        this.deleteCatalogItemFromState(catalogItemId);

        // Handle cascading deletes
        if (result?.categoryId != null) {
          const { useCategoriesStore } = await import("./categories");
          const categoriesStore = useCategoriesStore();
          categoriesStore.deleteCategory(result.categoryId);
        }
        if (result?.checklistId != null) {
          const { useChecklistsStore } = await import("./checklists");
          const checklistsStore = useChecklistsStore();
          checklistsStore.deleteChecklist(result.checklistId);
        }
        if (result?.checklistRowId != null) {
          const { useChecklistRowsStore } = await import("./checklistRows");
          const checklistRowsStore = useChecklistRowsStore();
          checklistRowsStore.deleteChecklistRow(result.checklistRowId);
        }
      },
    },
  }),
);
